import { useState } from 'react';
import Modal from 'react-modal';

const FAQ_DATA = [
    {
        category: '📚 General Questions',
        items: [
            { q: 'What is this Learning Portal?', a: 'This portal is an online platform where you can access courses, track your progress, and improve your skills.' },
            { q: 'How do I enroll in a course?', a: 'Go to All Courses, select a course, and click on Enroll or Request Course.' },
            { q: 'Are all courses free?', a: 'Some courses are free, while others may require approval from your manager.' },
        ],
    },
    {
        category: '👤 My Learning',
        items: [
            { q: 'Where can I see my assigned courses?', a: 'You can check them under the Assigned section.' },
            { q: 'How do I track my progress?', a: 'Go to In Progress to see your current course status.' },
            { q: 'Where can I find completed courses?', a: 'All completed courses are available in the Completed section.' },
        ],
    },
    {
        category: '📝 Course Requests',
        items: [
            { q: 'How can I request a course?', a: 'Go to All Courses, select a course, and click on Request Course.' },
            { q: 'Who approves my course request?', a: 'Your Reporting Manager (RM) will review and approve your request.' },
            { q: 'How do I check request status?', a: 'Go to Requested Courses to see the status.' },
        ],
    },
    {
        category: '📜 Certificates',
        items: [
            { q: 'When will I get my certificate?', a: 'You will receive a certificate after successfully completing a course.' },
            { q: 'Where can I download my certificate?', a: 'Go to My Certificates section to view and download.' },
        ],
    },
];

export default function HelpFAQModal({ isOpen, onClose }) {
    const [selectedCategory, setSelectedCategory] = useState(FAQ_DATA[0].category);
    const [openIndex, setOpenIndex] = useState(null);

    const currentFAQs = FAQ_DATA.find(f => f.category === selectedCategory)?.items || [];

    const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="FAQs"
            style={{
                overlay: { backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                content: {
                    position: 'relative', inset: 'unset',
                    width: '100%', maxWidth: '560px',
                    maxHeight: '85vh', borderRadius: '14px',
                    padding: '0', border: 'none',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    overflow: 'hidden', fontFamily: 'Inter, sans-serif',
                },
            }}
        >
            {/* Header */}
            <div style={headerStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '22px' }}>💬</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#fff' }}>Frequently Asked Questions</h2>
                        <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>Find answers to common questions</p>
                    </div>
                </div>
                <button onClick={onClose} style={closeBtnStyle}>✕</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(85vh - 80px)', backgroundColor: '#f8fafc' }}>
                {/* Category Dropdown */}
                <div style={{ marginBottom: '18px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                        Category
                    </label>
                    <div style={{ position: 'relative' }}>
                        <select
                            value={selectedCategory}
                            onChange={e => { setSelectedCategory(e.target.value); setOpenIndex(null); }}
                            style={selectStyle}
                        >
                            {FAQ_DATA.map(f => (
                                <option key={f.category} value={f.category}>{f.category}</option>
                            ))}
                        </select>
                        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#667085' }}>▾</span>
                    </div>
                </div>

                {/* FAQ Accordion */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {currentFAQs.map((faq, i) => (
                        <div key={i} style={{ ...cardStyle, ...(openIndex === i ? cardActiveStyle : {}) }}>
                            <button onClick={() => toggle(i)} style={questionBtnStyle}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <span style={qBadgeStyle}>Q{i + 1}</span>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a2332', textAlign: 'left', lineHeight: '1.4' }}>{faq.q}</span>
                                </div>
                                <span style={{ fontSize: '18px', color: '#667085', flexShrink: 0, transition: 'transform 0.2s', transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)' }}>⌄</span>
                            </button>
                            {openIndex === i && (
                                <div style={answerStyle}>
                                    <div style={{ width: '2px', backgroundColor: '#074870', borderRadius: '2px', flexShrink: 0, alignSelf: 'stretch' }} />
                                    <p style={{ margin: 0, fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>{faq.a}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{ marginTop: '20px', textAlign: 'center', padding: '12px', backgroundColor: '#eef2f7', borderRadius: '8px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#667085' }}>
                        Still have questions? Use <strong>Contact Admin</strong> once available.
                    </p>
                </div>
            </div>
        </Modal>
    );
}

const headerStyle = {
    background: 'linear-gradient(135deg, #074870 0%, #0c6fad 100%)',
    padding: '18px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const closeBtnStyle = {
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: '#fff', width: '30px', height: '30px',
    borderRadius: '50%', cursor: 'pointer', fontSize: '14px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const selectStyle = {
    width: '100%', padding: '10px 36px 10px 12px',
    borderRadius: '8px', border: '1px solid #d0d5dd',
    backgroundColor: '#fff', fontSize: '14px', color: '#1a2332',
    fontWeight: '500', appearance: 'none', cursor: 'pointer',
    outline: 'none', fontFamily: 'Inter, sans-serif',
};

const cardStyle = {
    backgroundColor: '#fff', borderRadius: '10px',
    border: '1px solid #e4e7ec',
    overflow: 'hidden', transition: 'box-shadow 0.2s',
};

const cardActiveStyle = {
    boxShadow: '0 2px 8px rgba(7,72,112,0.12)',
    border: '1px solid #b3d4ec',
};

const questionBtnStyle = {
    width: '100%', background: 'none', border: 'none',
    padding: '14px 16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
};

const qBadgeStyle = {
    fontSize: '11px', fontWeight: '700', color: '#074870',
    backgroundColor: '#e0f0ff', borderRadius: '4px',
    padding: '2px 6px', flexShrink: 0, marginTop: '1px',
};

const answerStyle = {
    display: 'flex', gap: '12px',
    padding: '0 16px 14px 16px',
    borderTop: '1px solid #f0f4f8',
    paddingTop: '12px',
};
